(() => {
  const SELECTOR =
    '[data-feature-carousel]';

  const DEPENDENCY_RETRY_LIMIT = 40;
  const DEPENDENCY_RETRY_DELAY = 100;

  const reducedMotionQuery =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  const instances = new Map();

  let dependencyRetryTimer = null;
  let dependencyRetryAttempts = 0;


  /* =========================================
     HELPERS
     ========================================= */

  function prefersReducedMotion() {
    return reducedMotionQuery.matches;
  }

  function padNumber(value) {
    return String(value).padStart(
      2,
      '0'
    );
  }

  function getCarousels(
    root = document
  ) {
    const carousels = [];

    if (
      root instanceof Element &&
      root.matches(SELECTOR)
    ) {
      carousels.push(root);
    }

    if (root.querySelectorAll) {
      root
        .querySelectorAll(SELECTOR)
        .forEach((carousel) => {
          carousels.push(carousel);
        });
    }

    return carousels;
  }

  function hasDependencies() {
    return Boolean(
      window.gsap &&
      window.Draggable &&
      window.InertiaPlugin
    );
  }

  function registerDependencies() {
    if (!hasDependencies()) {
      return false;
    }

    gsap.registerPlugin(
      Draggable,
      InertiaPlugin
    );

    return true;
  }

  function pruneDisconnectedInstances() {
    instances.forEach(
      (instance, root) => {
        if (!root.isConnected) {
          destroyFeatureCarousel(
            instance
          );
        }
      }
    );
  }


  /* =========================================
     CREATE
     ========================================= */

  function createFeatureCarousel(root) {
    if (!registerDependencies()) {
      return null;
    }

    const viewport =
      root.querySelector(
        '[data-slider="viewport"]'
      );

    const list =
      root.querySelector(
        '[data-slider="list"]'
      );

    const slides =
      gsap.utils.toArray(
        root.querySelectorAll(
          '[data-slider="slide"]'
        )
      );

    const selectButtons =
      Array.from(
        root.querySelectorAll(
          '[data-slider-select]'
        )
      );

    const nextButton =
      root.querySelector(
        '[data-slider-button="next"]'
      );

    const prevButton =
      root.querySelector(
        '[data-slider-button="prev"]'
      );

    const dots =
      Array.from(
        root.querySelectorAll(
          '[data-slider-dot]'
        )
      );

    const totalElement =
      root.querySelector(
        '[data-slide-count="total"]'
      );

    const stepElement =
      root.querySelector(
        '[data-slide-count="step"]'
      );

    if (
      !viewport ||
      !list ||
      !slides.length
    ) {
      return null;
    }

    const controller =
      new AbortController();

    const { signal } = controller;

    const instance = {
      root,
      viewport,
      list,
      slides,
      controller,
      loop: null,
    };

    if (totalElement) {
      totalElement.textContent =
        padNumber(slides.length);
    }

    let activeIndex = 0;
    let activeElement = null;


    /* =========================================
       MEDIA STATE
       ========================================= */

    function pauseVideos() {
      slides.forEach((slide) => {
        const video =
          slide.querySelector('video');

        if (video) {
          video.pause();
        }
      });
    }

    function updateVideos(activeSlide) {
      if (
        prefersReducedMotion() ||
        document.hidden
      ) {
        pauseVideos();
        return;
      }

      slides.forEach((slide) => {
        const video =
          slide.querySelector('video');

        if (!video) {
          return;
        }

        if (slide === activeSlide) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }


    /* =========================================
       ACTIVE STATE
       ========================================= */

    function updateDots(index) {
      dots.forEach((dot, dotIndex) => {
        const isActive =
          dotIndex === index;

        dot.classList.toggle(
          'is-active',
          isActive
        );

        if (isActive) {
          dot.setAttribute(
            'aria-current',
            'true'
          );
        } else {
          dot.removeAttribute(
            'aria-current'
          );
        }
      });
    }

    function applyActive(
      element,
      index
    ) {
      if (activeElement) {
        activeElement.classList.remove(
          'is-active'
        );
      }

      slides.forEach((slide) => {
        slide.removeAttribute(
          'aria-current'
        );
      });

      element.classList.add(
        'is-active'
      );

      element.setAttribute(
        'aria-current',
        'true'
      );

      activeElement = element;
      activeIndex = index;

      if (stepElement) {
        stepElement.textContent =
          padNumber(index + 1);
      }

      updateDots(index);
      updateVideos(element);
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        pauseVideos();
        return;
      }

      if (activeElement) {
        updateVideos(activeElement);
      }
    }

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange,
      {
        signal,
      }
    );


    /* =========================================
       SINGLE SLIDE
       ========================================= */

    if (slides.length === 1) {
      applyActive(
        slides[0],
        0
      );

      return instance;
    }


    /* =========================================
       LOOP
       ========================================= */

    const animationDuration =
      prefersReducedMotion()
        ? 0
        : 0.725;

    const getTrackGap = () => {
      const styles =
        window.getComputedStyle(list);

      return (
        parseFloat(styles.columnGap) ||
        parseFloat(styles.gap) ||
        0
      );
    };

    const loop = horizontalLoop(
      slides,
      {
        paused: true,
        draggable: true,
        inertia: !prefersReducedMotion(),
        center: viewport,
        paddingRight: getTrackGap,
        onChange: (
          element,
          index
        ) => {
          applyActive(
            element,
            index
          );
        },
      }
    );

    instance.loop = loop;

    function goTo(index) {
      if (!loop) {
        return;
      }

      loop.toIndex(index, {
        ease: 'power3',
        duration: animationDuration,
      });
    }


    /* =========================================
       CARD SELECTION
       ========================================= */

    selectButtons.forEach(
      (button, index) => {
        button.addEventListener(
          'click',
          () => {
            if (index === activeIndex) {
              return;
            }

            goTo(index);
          },
          {
            signal,
          }
        );
      }
    );


    /* =========================================
       PAGINATION
       ========================================= */

    dots.forEach((dot, index) => {
      dot.addEventListener(
        'click',
        () => {
          goTo(index);
        },
        {
          signal,
        }
      );
    });


    /* =========================================
       NAVIGATION BUTTONS
       ========================================= */

    nextButton?.addEventListener(
      'click',
      () => {
        loop.next({
          ease: 'power3',
          duration: animationDuration,
        });
      },
      {
        signal,
      }
    );

    prevButton?.addEventListener(
      'click',
      () => {
        loop.previous({
          ease: 'power3',
          duration: animationDuration,
        });
      },
      {
        signal,
      }
    );


    /* =========================================
       KEYBOARD
       ========================================= */

    viewport.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();

          loop.next({
            ease: 'power3',
            duration: animationDuration,
          });

          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();

          loop.previous({
            ease: 'power3',
            duration: animationDuration,
          });

          return;
        }

        if (event.key === 'Home') {
          event.preventDefault();
          goTo(0);
          return;
        }

        if (event.key === 'End') {
          event.preventDefault();
          goTo(slides.length - 1);
        }
      },
      {
        signal,
      }
    );


    /* =========================================
       INITIAL SLIDE
       ========================================= */

    let initialIndex =
      Number.parseInt(
        root.dataset.initialSlide,
        10
      ) - 1;

    if (Number.isNaN(initialIndex)) {
      initialIndex = 0;
    }

    initialIndex = Math.min(
      Math.max(initialIndex, 0),
      slides.length - 1
    );

    loop.toIndex(initialIndex, {
      duration: 0,
    });

    return instance;
  }


  /* =========================================
     DESTROY
     ========================================= */

  function destroyFeatureCarousel(
    instance
  ) {
    if (!instance) {
      return;
    }

    instance.controller.abort();

    instance.slides.forEach((slide) => {
      const video =
        slide.querySelector('video');

      if (video) {
        video.pause();
      }

      slide.classList.remove(
        'is-active'
      );

      slide.removeAttribute(
        'aria-current'
      );
    });

    if (instance.loop?.draggable) {
      instance.loop.draggable.kill();
    }

    if (instance.loop?.wamboltCleanup) {
      instance.loop.wamboltCleanup();
    }

    instance.loop?.kill();

    gsap.killTweensOf(
      instance.slides
    );

    gsap.set(instance.slides, {
      clearProps: 'transform',
    });

    instances.delete(instance.root);
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function initFeatureCarousels(
    root = document
  ) {
    const carousels =
      getCarousels(root);

    if (!carousels.length) {
      return true;
    }

    if (!registerDependencies()) {
      return false;
    }

    pruneDisconnectedInstances();

    carousels.forEach((carousel) => {
      const existing =
        instances.get(carousel);

      if (existing) {
        destroyFeatureCarousel(
          existing
        );
      }

      const instance =
        createFeatureCarousel(carousel);

      if (instance) {
        instances.set(
          carousel,
          instance
        );
      }
    });

    return true;
  }

  function destroyFeatureCarousels(
    root = document
  ) {
    getCarousels(root).forEach(
      (carousel) => {
        const instance =
          instances.get(carousel);

        if (instance) {
          destroyFeatureCarousel(
            instance
          );
        }
      }
    );
  }

  function rebuildFeatureCarousels() {
    Array.from(
      instances.values()
    ).forEach(
      destroyFeatureCarousel
    );

    if (!initFeatureCarousels(document)) {
      scheduleDependencyRetry();
    }
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearDependencyRetry() {
    if (dependencyRetryTimer !== null) {
      window.clearTimeout(
        dependencyRetryTimer
      );

      dependencyRetryTimer = null;
    }
  }

  function scheduleDependencyRetry() {
    if (dependencyRetryTimer !== null) {
      return;
    }

    if (
      dependencyRetryAttempts >=
      DEPENDENCY_RETRY_LIMIT
    ) {
      console.warn(
        'Wambolt Feature Carousel could not initialize because GSAP, Draggable, or InertiaPlugin is unavailable.'
      );

      return;
    }

    dependencyRetryAttempts += 1;

    dependencyRetryTimer =
      window.setTimeout(
        () => {
          dependencyRetryTimer = null;

          if (
            initFeatureCarousels(
              document
            )
          ) {
            dependencyRetryAttempts = 0;
            clearDependencyRetry();
            return;
          }

          scheduleDependencyRetry();
        },
        DEPENDENCY_RETRY_DELAY
      );
  }

  function start() {
    if (
      initFeatureCarousels(document)
    ) {
      dependencyRetryAttempts = 0;
      clearDependencyRetry();
      return;
    }

    scheduleDependencyRetry();
  }

  function horizontalLoop(
    items,
    config
  ) {
    let timeline;


    items =
      gsap.utils.toArray(
        items
      );

    config =
      config || {};


    gsap.context(
      () => {
        let onChange =
            config.onChange,

          lastIndex = 0,

          tl =
            gsap.timeline(
              {
                repeat:
                  config.repeat,

                paused:
                  config.paused,

                defaults: {
                  ease: 'none'
                },

                onUpdate:
                  onChange
                    ? function () {
                        const index =
                          tl.closestIndex();


                        if (
                          lastIndex !==
                          index
                        ) {
                          lastIndex =
                            index;

                          onChange(
                            items[index],
                            index
                          );
                        }
                      }
                    : null,

                onReverseComplete:
                  () => {
                    tl.totalTime(
                      tl.rawTime() +
                      tl.duration() *
                        100
                    );
                  }
              }
            ),

          length =
            items.length,

          startX =
            items[0]
              .offsetLeft,

          times = [],

          widths = [],

          spaceBefore = [],

          xPercents = [],

          currentIndex = 0,

          indexIsDirty =
            false,

          center =
            config.center,

          pixelsPerSecond =
            (
              config.speed ||
              1
            ) * 100,

          snap =
            config.snap ===
            false
              ? (value) =>
                  value
              : gsap.utils.snap(
                  config.snap ||
                  1
                ),

          timeOffset = 0,

          container =
            center === true
              ? items[0]
                  .parentNode
              : gsap.utils.toArray(
                  center
                )[0] ||
                items[0]
                  .parentNode,

          totalWidth,

          timeWrap,

          proxy,

          resizeHandler;


        const getPaddingRight =
          () => {
            const value =
              typeof config.paddingRight ===
              'function'
                ? config.paddingRight()
                : config.paddingRight;

            return (
              parseFloat(
                value
              ) || 0
            );
          };


        const getTotalWidth =
          () =>
            items[
              length - 1
            ].offsetLeft +
            (
              xPercents[
                length - 1
              ] /
              100
            ) *
              widths[
                length - 1
              ] -
            startX +
            spaceBefore[0] +
            items[
              length - 1
            ].offsetWidth *
              gsap.getProperty(
                items[
                  length - 1
                ],
                'scaleX'
              ) +
            getPaddingRight();


        const populateWidths =
          () => {
            let previousBounds =
              container
                .getBoundingClientRect();

            let currentBounds;


            items.forEach(
              (
                element,
                index
              ) => {
                widths[index] =
                  parseFloat(
                    gsap.getProperty(
                      element,
                      'width',
                      'px'
                    )
                  );


                xPercents[index] =
                  snap(
                    (
                      parseFloat(
                        gsap.getProperty(
                          element,
                          'x',
                          'px'
                        )
                      ) /
                      widths[index]
                    ) *
                      100 +
                    gsap.getProperty(
                      element,
                      'xPercent'
                    )
                  );


                currentBounds =
                  element
                    .getBoundingClientRect();


                spaceBefore[index] =
                  currentBounds.left -
                  (
                    index
                      ? previousBounds.right
                      : previousBounds.left
                  );


                previousBounds =
                  currentBounds;
              }
            );


            gsap.set(
              items,
              {
                xPercent:
                  (index) =>
                    xPercents[
                      index
                    ]
              }
            );


            totalWidth =
              getTotalWidth();
          };


        const populateOffsets =
          () => {
            timeOffset =
              center
                ? (
                    tl.duration() *
                    (
                      container
                        .offsetWidth /
                      2
                    )
                  ) /
                  totalWidth
                : 0;


            if (center) {
              times.forEach(
                (
                  time,
                  index
                ) => {
                  times[index] =
                    timeWrap(
                      tl.labels[
                        `label${index}`
                      ] +
                      (
                        tl.duration() *
                        widths[
                          index
                        ]
                      ) /
                        2 /
                        totalWidth -
                      timeOffset
                    );
                }
              );
            }
          };


        const getClosest =
          (
            values,
            value,
            wrap
          ) => {
            let index =
              values.length;

            let closest =
              1e10;

            let closestIndex =
              0;

            let distance;


            while (index--) {
              distance =
                Math.abs(
                  values[index] -
                  value
                );


              if (
                distance >
                wrap / 2
              ) {
                distance =
                  wrap -
                  distance;
              }


              if (
                distance <
                closest
              ) {
                closest =
                  distance;

                closestIndex =
                  index;
              }
            }


            return closestIndex;
          };


        const populateTimeline =
          () => {
            let index;

            let item;

            let currentX;

            let distanceToStart;

            let distanceToLoop;


            tl.clear();


            for (
              index = 0;
              index < length;
              index++
            ) {
              item =
                items[index];


              currentX =
                (
                  xPercents[
                    index
                  ] /
                  100
                ) *
                widths[index];


              distanceToStart =
                item.offsetLeft +
                currentX -
                startX +
                spaceBefore[0];


              distanceToLoop =
                distanceToStart +
                widths[index] *
                  gsap.getProperty(
                    item,
                    'scaleX'
                  );


              tl
                .to(
                  item,
                  {
                    xPercent:
                      snap(
                        (
                          currentX -
                          distanceToLoop
                        ) /
                          widths[
                            index
                          ] *
                          100
                      ),

                    duration:
                      distanceToLoop /
                      pixelsPerSecond
                  },
                  0
                )
                .fromTo(
                  item,
                  {
                    xPercent:
                      snap(
                        (
                          currentX -
                          distanceToLoop +
                          totalWidth
                        ) /
                          widths[
                            index
                          ] *
                          100
                      )
                  },
                  {
                    xPercent:
                      xPercents[
                        index
                      ],

                    duration:
                      (
                        currentX -
                        distanceToLoop +
                        totalWidth -
                        currentX
                      ) /
                      pixelsPerSecond,

                    immediateRender:
                      false
                  },
                  distanceToLoop /
                    pixelsPerSecond
                )
                .add(
                  `label${index}`,
                  distanceToStart /
                    pixelsPerSecond
                );


              times[index] =
                distanceToStart /
                pixelsPerSecond;
            }


            timeWrap =
              gsap.utils.wrap(
                0,
                tl.duration()
              );
          };


        const refresh =
          (deep) => {
            const progress =
              tl.progress();


            tl.progress(
              0,
              true
            );


            populateWidths();


            if (deep) {
              populateTimeline();
            }


            populateOffsets();


            if (
              deep &&
              tl.draggable
            ) {
              tl.time(
                times[
                  currentIndex
                ],
                true
              );
            } else {
              tl.progress(
                progress,
                true
              );
            }
          };


        resizeHandler =
          () => {
            refresh(
              true
            );
          };


        gsap.set(
          items,
          {
            x: 0
          }
        );


        populateWidths();

        populateTimeline();

        populateOffsets();


        window.addEventListener(
          'resize',
          resizeHandler
        );


        function toIndex(
          index,
          vars
        ) {
          vars =
            vars || {};


          if (
            Math.abs(
              index -
              currentIndex
            ) >
            length / 2
          ) {
            index +=
              index >
              currentIndex
                ? -length
                : length;
          }


          const newIndex =
            gsap.utils.wrap(
              0,
              length,
              index
            );


          let time =
            times[
              newIndex
            ];


          if (
            (
              time >
              tl.time()
            ) !==
              (
                index >
                currentIndex
              ) &&
            index !==
              currentIndex
          ) {
            time +=
              tl.duration() *
              (
                index >
                currentIndex
                  ? 1
                  : -1
              );
          }


          if (
            time < 0 ||
            time >
              tl.duration()
          ) {
            vars.modifiers = {
              time:
                timeWrap
            };
          }


          currentIndex =
            newIndex;


          vars.overwrite =
            true;


          gsap.killTweensOf(
            proxy
          );


          return vars.duration ===
            0
            ? tl.time(
                timeWrap(
                  time
                )
              )
            : tl.tweenTo(
                time,
                vars
              );
        }


        tl.toIndex =
          (
            index,
            vars
          ) =>
            toIndex(
              index,
              vars
            );


        tl.closestIndex =
          (
            setCurrent
          ) => {
            const index =
              getClosest(
                times,
                tl.time(),
                tl.duration()
              );


            if (setCurrent) {
              currentIndex =
                index;

              indexIsDirty =
                false;
            }


            return index;
          };


        tl.current =
          () =>
            indexIsDirty
              ? tl.closestIndex(
                  true
                )
              : currentIndex;


        tl.next =
          (vars) =>
            toIndex(
              tl.current() + 1,
              vars
            );


        tl.previous =
          (vars) =>
            toIndex(
              tl.current() - 1,
              vars
            );


        tl.times =
          times;


        tl
          .progress(
            1,
            true
          )
          .progress(
            0,
            true
          );


        if (
          config.reversed
        ) {
          tl.vars
            .onReverseComplete();

          tl.reverse();
        }


        if (
          config.draggable &&
          typeof Draggable ===
            'function'
        ) {
          proxy =
            document.createElement(
              'div'
            );


          const wrap =
            gsap.utils.wrap(
              0,
              1
            );


          let ratio;

          let startProgress;

          let draggable;

          let lastSnap;

          let initialChangeX;

          let wasPlaying;


          const align =
            () => {
              tl.progress(
                wrap(
                  startProgress +
                  (
                    draggable
                      .startX -
                    draggable.x
                  ) *
                    ratio
                )
              );
            };


          const syncIndex =
            () => {
              tl.closestIndex(
                true
              );
            };


          draggable =
            Draggable.create(
              proxy,
              {
                trigger:
                  container,

                type: 'x',

                dragClickables:
                  true,

                onPressInit() {
                  const x =
                    this.x;


                  gsap.killTweensOf(
                    tl
                  );


                  wasPlaying =
                    !tl.paused();


                  tl.pause();


                  startProgress =
                    tl.progress();


                  refresh();


                  ratio =
                    1 /
                    totalWidth;


                  initialChangeX =
                    (
                      startProgress /
                      -ratio
                    ) -
                    x;


                  gsap.set(
                    proxy,
                    {
                      x:
                        startProgress /
                        -ratio
                    }
                  );
                },

                onDrag:
                  align,

                onThrowUpdate:
                  align,

                overshootTolerance:
                  0,

                inertia:
                  config.inertia !== false,

                /*
                 * Preserve direct pointer response while
                 * limiting momentum after release.
                 */
                throwResistance:
                  4200,

                /* Limit long inertial glides. */
                minDuration:
                  0.18,

                maxDuration:
                  0.75,

                snap(value) {
                  if (
                    Math.abs(
                      startProgress /
                        -ratio -
                        this.x
                    ) <
                    10
                  ) {
                    return (
                      lastSnap +
                      initialChangeX
                    );
                  }


                  const time =
                    -(
                      value *
                      ratio
                    ) *
                    tl.duration();


                  const wrappedTime =
                    timeWrap(
                      time
                    );


                  const snapTime =
                    times[
                      getClosest(
                        times,
                        wrappedTime,
                        tl.duration()
                      )
                    ];


                  let difference =
                    snapTime -
                    wrappedTime;


                  if (
                    Math.abs(
                      difference
                    ) >
                    tl.duration() /
                      2
                  ) {
                    difference +=
                      difference < 0
                        ? tl.duration()
                        : -tl.duration();
                  }


                  lastSnap =
                    (
                      time +
                      difference
                    ) /
                    tl.duration() /
                    -ratio;


                  return lastSnap;
                },

                onRelease() {
                  syncIndex();


                  if (
                    draggable
                      .isThrowing
                  ) {
                    indexIsDirty =
                      true;

                    return;
                  }


                  if (
                    config.inertia ===
                      false
                  ) {
                    tl.toIndex(
                      tl.current(),
                      {
                        duration: 0
                      }
                    );
                  }
                },

                onThrowComplete:
                  () => {
                    syncIndex();


                    if (
                      wasPlaying
                    ) {
                      tl.play();
                    }
                  }
              }
            )[0];


          tl.draggable =
            draggable;
        }


        tl.closestIndex(
          true
        );


        lastIndex =
          currentIndex;


        if (onChange) {
          onChange(
            items[
              currentIndex
            ],
            currentIndex
          );
        }


        tl.wamboltCleanup =
          () => {
            window.removeEventListener(
              'resize',
              resizeHandler
            );
          };


        timeline =
          tl;
      }
    );


    return timeline;
  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }


  /* =========================================
     REDUCED MOTION CHANGES
     ========================================= */

  if (
    typeof reducedMotionQuery.addEventListener ===
    'function'
  ) {
    reducedMotionQuery.addEventListener(
      'change',
      rebuildFeatureCarousels
    );
  } else if (
    typeof reducedMotionQuery.addListener ===
    'function'
  ) {
    reducedMotionQuery.addListener(
      rebuildFeatureCarousels
    );
  }


  /* =========================================
     SHOPIFY / BARBA LIFECYCLE
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (
        !initFeatureCarousels(
          event.target
        )
      ) {
        scheduleDependencyRetry();
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyFeatureCarousels(
        event.target
      );
    }
  );
})();